export type BrandConfig = {
  key: string;
  appTitle: string;
  displayName: string;
  certificateLogoSrc?: string;
  supportBaseUrl?: string;
  defaultLocale?: string;
};

export type BrandVisibilityRule = {
  showForBrands?: string[];
  hideForBrands?: string[];
};

export type BrandFeatures = {
  faceIdIdentificationOverlayEnabled: boolean;
  biometricResetEnabled: boolean;
  biometricConsentRequired: boolean;
  gbdflRequestEnabled: boolean;
  /* Чат: сообщение во всплывашке целиком, ссылки, копирование, перенос длинных
     слов; студенту не всплывают сообщения завершённого задания. Только
     app.trustexam.ai — остальные бренды видят чат как раньше. */
  chatRichMessagesEnabled: boolean;
};

const DEFAULT_BRAND_KEY = "trustexam";
const SYSTEMS247_BRAND_KEY = "systems247";
const BRAND_KEY_ALIASES: Record<string, string> = {
  trustexam: DEFAULT_BRAND_KEY,
  trustexamai: DEFAULT_BRAND_KEY,
  examru: "examru",
  demo: "demo",
  system247: SYSTEMS247_BRAND_KEY,
  systems247: SYSTEMS247_BRAND_KEY,
};

const DEFAULT_BRAND_FEATURES: BrandFeatures = {
  faceIdIdentificationOverlayEnabled: true,
  biometricResetEnabled: false,
  biometricConsentRequired: false,
  gbdflRequestEnabled: true,
  chatRichMessagesEnabled: false,
};

const BRAND_FEATURES: Record<string, Partial<BrandFeatures>> = {
  trustexam: {
    biometricResetEnabled: true,
    biometricConsentRequired: true,
    chatRichMessagesEnabled: true,
  },
  examru: {
    faceIdIdentificationOverlayEnabled: false,
    biometricResetEnabled: false,
    biometricConsentRequired: false,
    gbdflRequestEnabled: false,
  },
  demo: {
    faceIdIdentificationOverlayEnabled: false,
    biometricResetEnabled: false,
    biometricConsentRequired: false,
  },
  systems247: {
    faceIdIdentificationOverlayEnabled: false,
    biometricResetEnabled: true,
    biometricConsentRequired: true,
  },
};

export const BRANDS: Record<string, BrandConfig> = {
  trustexam: {
    key: "trustexam",
    appTitle: "TrustExam",
    displayName: "TrustExam",
    certificateLogoSrc: "/TrustExam.png",
    supportBaseUrl: "https://support.trustexam.ai",
  },
  examru: {
    key: "examru",
    appTitle: "Честный Экзамен",
    displayName: "Честный Экзамен",
    certificateLogoSrc: "/examru-logo-dark.svg",
    supportBaseUrl: "https://support.trustexam.ai",
    defaultLocale: "ru",
  },
  demo: {
    key: "demo",
    appTitle: "Demo",
    displayName: "Demo",
    // NOTE: add real asset if needed
    // certificateLogoSrc: "/demo-logo.png",
    // supportBaseUrl: "https://support.example.com",
  },
};

function normalizeBrandKey(brandKey: string | null | undefined): string {
  const raw = (brandKey ?? "").trim().toLowerCase();

  if (!raw) return DEFAULT_BRAND_KEY;

  return BRAND_KEY_ALIASES[raw] ?? DEFAULT_BRAND_KEY;
}

export function getBrandKeyFromEnv(): string {
  return normalizeBrandKey(
    process.env.NEXT_PUBLIC_BRAND_KEY ?? process.env.BRAND_KEY ?? ""
  );
}

export function getBrandFeatures(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): BrandFeatures {
  const key = normalizeBrandKey(brandKey);

  return {
    ...DEFAULT_BRAND_FEATURES,
    ...(BRAND_FEATURES[key] ?? {}),
  };
}

export function getBrandConfigByKey(brandKey: string | null | undefined): BrandConfig {
  const key = normalizeBrandKey(brandKey);
  return BRANDS[key] ?? BRANDS[DEFAULT_BRAND_KEY];
}

export function getBrandConfig(): BrandConfig {
  return getBrandConfigByKey(getBrandKeyFromEnv());
}

export function isSystems247Brand(): boolean {
  return getBrandKeyFromEnv() === SYSTEMS247_BRAND_KEY;
}

export function getBrandDefaultLocale(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): string | undefined {
  return getBrandConfigByKey(brandKey).defaultLocale;
}

export function getBrandI18nKey(baseKey: string): string {
  const brandKey = getBrandKeyFromEnv();
  const normalized = brandKey === "examru" ? "examru" : "trustexam";
  return `${baseKey}-${normalized}`;
}

export function isFaceIdIdentificationOverlayEnabled(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return getBrandFeatures(brandKey).faceIdIdentificationOverlayEnabled;
}

export function isBiometricResetEnabled(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return getBrandFeatures(brandKey).biometricResetEnabled;
}

export function isBiometricConsentRequired(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return getBrandFeatures(brandKey).biometricConsentRequired;
}

export function isGbdflRequestEnabled(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return getBrandFeatures(brandKey).gbdflRequestEnabled;
}

export function isChatRichMessagesEnabled(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return getBrandFeatures(brandKey).chatRichMessagesEnabled;
}

/**
 * Отчёт ИИ запускается только по кнопке «Сгенерировать» — во всех брендах.
 *
 * Раньше ручной режим был только у examru, а остальные бренды начинали генерацию
 * сами при открытии вкладки. Это расходилось с тем, что обещает сам дровер
 * («ждём явного клика, никаких авто-запросов»), и приводило к генерации отчёта по
 * попытке, которую студент ещё сдаёт: данные в этот момент только пишутся.
 */
export function isManualReportGeneration(): boolean {
  return true;
}

export function isFaqManagerEnabled(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return normalizeBrandKey(brandKey) === "examru";
}

export function isQuizToolsFabEnabled(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return normalizeBrandKey(brandKey) !== "examru";
}

export function isAlphaDifficultyLevelsEnabled(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return normalizeBrandKey(brandKey) !== "examru";
}

export function isCompletedTimeAlwaysVisible(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return normalizeBrandKey(brandKey) === "examru";
}

/* Контакты поддержки школы (school_contacts) менеджер заводит только в кабинетах trustexam. */
export function isSchoolSupportContactsEnabled(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return normalizeBrandKey(brandKey) === DEFAULT_BRAND_KEY;
}

export function isVisibleForBrand(
  rule: BrandVisibilityRule,
  brandKey: string
): boolean {
  const normalizedBrandKey = normalizeBrandKey(brandKey);

  if (rule.showForBrands?.length) {
    return rule.showForBrands.includes(normalizedBrandKey);
  }

  if (rule.hideForBrands?.length) {
    return !rule.hideForBrands.includes(normalizedBrandKey);
  }

  return true;
}
